import { Flex, Tag } from 'antd';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { useTranslation } from 'translations/hooks';

import { useGraphqlApiHeaders } from 'business/contextProviders/useApiHeaders';
import { useAppContext } from 'business/contextProviders/useAppContext';
import { DATA_TRANSMISSION_ONLINE_THRESHOLD } from 'business/dashboard/constants';
import { Module_Enum, useGetTbmLastMetricQuery } from 'generated/graphql';
import { useMediaType } from 'technical/media/hooks';
import { minutesToMilliseconds } from 'technical/time-utils/minutes-to-milliseconds';
import { useCurrentDate } from 'technical/time-utils/use-current-date';
import { isUndefinedOrNull } from 'technical/utils/is-null-or-undefined';
import { StatusDot, StatusDotStatuses } from 'ui/status-dot';

import styles from './index.module.scss';

export enum State {
  excavating = 'excavating',
  building = 'building',
  stopped = 'stopped',
  completed = 'completed',
}

interface DateProps {
  timeZone?: string;
}

interface StateProps {
  state?: State;
  onlineTransmission: boolean;
}

const stateTag = {
  [State.excavating]: {
    color: 'green',
    translationKey: 'dashboard.tbm.state.excavating',
  },
  [State.building]: {
    color: 'gold',
    translationKey: 'dashboard.tbm.state.building',
  },
  [State.stopped]: {
    color: undefined,
    translationKey: 'dashboard.tbm.state.stopped',
  },
  [State.completed]: {
    color: undefined,
    translationKey: 'dashboard.tbm.state.completed',
  },
};

const LastDate = ({ timeZone }: DateProps) => {
  const { t } = useTranslation();
  const { isMobile } = useMediaType();

  const currentDate = dayjs().tz(timeZone);
  const compareDate = currentDate.format('YYYY-MM-DDTHH');

  const date = useMemo(
    () => {
      return currentDate.format('LL LTS');
    },
    // This is intentional; we want to display the full date without
    // state refreshes for minute or second changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [compareDate],
  );

  return (
    <Tag bordered={false} className={styles.corner}>
      <Flex gap={8}>
        {isMobile ? null : t('dashboard.tbm.localDate')}
        <span className={styles.date}>{timeZone ? date : '-'}</span>
      </Flex>
    </Tag>
  );
};

const LastState = ({ state, onlineTransmission }: StateProps) => {
  const { t } = useTranslation();
  const { isMobile } = useMediaType();

  if (!onlineTransmission && state !== State.completed) {
    return null;
  }

  return (
    <Tag
      color={state ? stateTag[state].color : undefined}
      bordered={false}
      className={styles.corner}
    >
      <Flex gap={8}>
        {isMobile ? null : (
          <span className={styles.statusLabel}>
            {t('dashboard.tbm.state.label')}
          </span>
        )}

        <span className={styles.statusValue}>
          {state ? t(stateTag[state].translationKey) : '-'}
        </span>
      </Flex>
    </Tag>
  );
};

export const LastKnownState = ({
  timeZone,
  state,
  onlineTransmission,
}: DateProps & StateProps) => {
  const { isTablet, isMobile } = useMediaType();

  return (
    <Flex gap={4} wrap={isTablet || isMobile}>
      <LastDate timeZone={timeZone} />
      <LastState state={state} onlineTransmission={onlineTransmission} />
    </Flex>
  );
};

export const DataTransmissionStatus = () => {
  const { currentConstructionSiteId } = useAppContext();
  const { currentDate } = useCurrentDate({
    refreshTimeout: minutesToMilliseconds(DATA_TRANSMISSION_ONLINE_THRESHOLD),
  });

  const headers = useGraphqlApiHeaders(Module_Enum.Dashboard);

  const { data } = useGetTbmLastMetricQuery({
    variables: {
      constructionSiteId: currentConstructionSiteId,
      compareDate: currentDate.subtract(
        DATA_TRANSMISSION_ONLINE_THRESHOLD,
        'minute',
      ),
    },
    context: {
      headers,
    },
    fetchPolicy: 'network-only',
  });

  let status: StatusDotStatuses = 'unknown';

  if (data?.parameter_metric_aggregate.aggregate?.max) {
    status = isUndefinedOrNull(
      data.parameter_metric_aggregate.aggregate.max.batchDate,
    )
      ? 'offline'
      : 'online';
  }

  return <StatusDot status={status} />;
};
